'use client';

import { useState, useEffect } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, FilterX, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { format } from 'date-fns/format';
import { useGroupBuyStore } from '@/stores/useGroupBuyStore';
import { JSX } from 'react';

// Product types for filtering
const PRODUCT_TYPES = [
  { value: 'lead_lists', label: 'Lead Lists' },
  { value: 'software', label: 'Software / SaaS Access' },
  { value: 'courses', label: 'Courses / E-Learning' },
  { value: 'info_products', label: 'Info Products' },
  { value: 'services', label: 'Services' },
  { value: 'subscriptions', label: 'Subscriptions' },
  { value: 'other', label: 'Other' },
];

// Status options
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Ending timeframe options
const ENDING_TIMEFRAMES = [
  { value: 'this_week', label: 'Ending this week' },
  { value: 'this_month', label: 'Ending this month' },
  { value: 'anytime', label: 'Anytime' },
];

interface GroupBuyFilterSidebarProps {
  className?: string;
  onMobileClose?: () => void;
  isMobile?: boolean;
}

export default function GroupBuyFilterSidebar({
  className,
  onMobileClose,
  isMobile = false,
}: GroupBuyFilterSidebarProps): JSX.Element {
  // Get values and actions from Zustand store
  const {
    productType,
    status,
    priceRange,
    targetQuantityRange,
    progressRange,
    endingTimeframe,
    endingDate,
    setProductType,
    setStatus,
    setPriceRange,
    setTargetQuantityRange,
    setProgressRange,
    setEndingTimeframe,
    setEndingDate,
    resetFilters: resetAllFilters,
    batchUpdateFilters,
    startBatchUpdate,
    endBatchUpdate,
  } = useGroupBuyStore();

  // Local UI state for form inputs
  const [selectedProductType, setSelectedProductType] = useState<string | undefined>(productType);
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(status);
  const [localPriceRange, setLocalPriceRange] = useState<{ min: string; max: string }>({
    min: priceRange.min?.toString() || '',
    max: priceRange.max?.toString() || '',
  });
  const [localTargetQuantityRange, setLocalTargetQuantityRange] = useState<{
    min: string;
    max: string;
  }>({
    min: targetQuantityRange.min?.toString() || '',
    max: targetQuantityRange.max?.toString() || '',
  });
  const [localProgressRange, setLocalProgressRange] = useState<{ min: string; max: string }>({
    min: progressRange.min?.toString() || '',
    max: progressRange.max?.toString() || '',
  });
  const [selectedEndingTimeframe, setSelectedEndingTimeframe] = useState<string | undefined>(
    endingTimeframe,
  );
  const [date, setDate] = useState<Date | undefined>(endingDate);

  // Update local state when store changes
  useEffect(() => {
    setSelectedProductType(productType);
    setSelectedStatus(status);
    setLocalPriceRange({
      min: priceRange.min?.toString() || '',
      max: priceRange.max?.toString() || '',
    });
    setLocalTargetQuantityRange({
      min: targetQuantityRange.min?.toString() || '',
      max: targetQuantityRange.max?.toString() || '',
    });
    setLocalProgressRange({
      min: progressRange.min?.toString() || '',
      max: progressRange.max?.toString() || '',
    });
    setSelectedEndingTimeframe(endingTimeframe);
    setDate(endingDate);
  }, [
    productType,
    status,
    priceRange,
    targetQuantityRange,
    progressRange,
    endingTimeframe,
    endingDate,
  ]);

  // Apply filters to store
  const applyFilters = () => {
    // Start batch update
    startBatchUpdate();

    // Update all filters
    setProductType(selectedProductType);
    setStatus(selectedStatus);

    // Update price range
    setPriceRange(
      localPriceRange.min ? parseFloat(localPriceRange.min) : undefined,
      localPriceRange.max ? parseFloat(localPriceRange.max) : undefined,
    );

    // Update target quantity range
    setTargetQuantityRange(
      localTargetQuantityRange.min ? parseFloat(localTargetQuantityRange.min) : undefined,
      localTargetQuantityRange.max ? parseFloat(localTargetQuantityRange.max) : undefined,
    );

    // Update progress range
    setProgressRange(
      localProgressRange.min ? parseFloat(localProgressRange.min) : undefined,
      localProgressRange.max ? parseFloat(localProgressRange.max) : undefined,
    );

    // Update ending timeframe and date
    setEndingTimeframe(selectedEndingTimeframe);
    setEndingDate(date);

    // End batch update
    endBatchUpdate();
  };

  // Apply a specific filter change immediately
  const applyFilterChange = (filterChange: () => void) => {
    startBatchUpdate();
    filterChange();
    endBatchUpdate();
  };

  // Reset all filters
  const resetFilters = () => {
    // Reset local state
    setSelectedProductType(undefined);
    setSelectedStatus('active');
    setLocalPriceRange({ min: '', max: '' });
    setLocalTargetQuantityRange({ min: '', max: '' });
    setLocalProgressRange({ min: '', max: '' });
    setSelectedEndingTimeframe(undefined);
    setDate(undefined);

    // Reset store state
    resetAllFilters();

    // Close mobile drawer if on mobile
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  // Toggle product type selection
  const toggleProductType = (typeValue: string) => {
    const newValue = selectedProductType === typeValue ? undefined : typeValue;
    setSelectedProductType(newValue);
    applyFilterChange(() => setProductType(newValue));
  };

  // Handle date selection for custom ending date
  const handleDateSelect = (newDate: Date | undefined) => {
    setDate(newDate);
    setSelectedEndingTimeframe(undefined);
    applyFilterChange(() => {
      setEndingDate(newDate);
      setEndingTimeframe(undefined);
    });
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      !!selectedProductType ||
      selectedStatus !== 'active' ||
      !!localPriceRange.min ||
      !!localPriceRange.max ||
      !!localTargetQuantityRange.min ||
      !!localTargetQuantityRange.max ||
      !!localProgressRange.min ||
      !!localProgressRange.max ||
      !!selectedEndingTimeframe ||
      !!date
    );
  };

  return (
    <div className={cn('flex flex-col space-y-6', className)}>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Product Type</h3>
          {selectedProductType && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() =>
                applyFilterChange(() => {
                  setSelectedProductType(undefined);
                  setProductType(undefined);
                })
              }
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="space-y-2.5">
          {PRODUCT_TYPES.map((type) => (
            <div key={type.value} className="flex items-center space-x-2">
              <Checkbox
                id={`product-type-${type.value}`}
                checked={selectedProductType === type.value}
                onCheckedChange={(checked) => {
                  if (checked) {
                    toggleProductType(type.value);
                  } else if (selectedProductType === type.value) {
                    toggleProductType(type.value);
                  }
                }}
              />
              <Label htmlFor={`product-type-${type.value}`} className="text-sm cursor-pointer">
                {type.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      {/* Status Section */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Status</h3>
          {selectedStatus && selectedStatus !== 'active' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() =>
                applyFilterChange(() => {
                  setSelectedStatus('active');
                  setStatus('active');
                })
              }
            >
              <X className="h-3 w-3 mr-1" />
              Reset
            </Button>
          )}
        </div>
        <RadioGroup
          value={selectedStatus}
          onValueChange={(value) => {
            setSelectedStatus(value);
            applyFilterChange(() => {
              setStatus(value);
            });
          }}
          className="space-y-2.5"
        >
          {STATUS_OPTIONS.map((option) => (
            <div key={option.value} className="flex items-center space-x-2">
              <RadioGroupItem value={option.value} id={`status-${option.value}`} />
              <Label htmlFor={`status-${option.value}`} className="text-sm cursor-pointer">
                {option.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Price Range Section */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Price Range (Per Participant)</h3>
          {(localPriceRange.min || localPriceRange.max) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                setLocalPriceRange({ min: '', max: '' });
                applyFilterChange(() => {
                  setPriceRange(undefined, undefined);
                });
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="price-min" className="text-xs">
              Min (£)
            </Label>
            <Input
              id="price-min"
              type="number"
              placeholder="Min"
              value={localPriceRange.min}
              onChange={(e) => setLocalPriceRange((prev) => ({ ...prev, min: e.target.value }))}
              onBlur={applyFilters}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="price-max" className="text-xs">
              Max (£)
            </Label>
            <Input
              id="price-max"
              type="number"
              placeholder="Max"
              value={localPriceRange.max}
              onChange={(e) => setLocalPriceRange((prev) => ({ ...prev, max: e.target.value }))}
              onBlur={applyFilters}
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Target Quantity Section */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Target Quantity Range</h3>
          {(localTargetQuantityRange.min || localTargetQuantityRange.max) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                setLocalTargetQuantityRange({ min: '', max: '' });
                applyFilterChange(() => {
                  setTargetQuantityRange(undefined, undefined);
                });
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="target-min" className="text-xs">
              Min
            </Label>
            <Input
              id="target-min"
              type="number"
              placeholder="Min"
              value={localTargetQuantityRange.min}
              onChange={(e) =>
                setLocalTargetQuantityRange((prev) => ({ ...prev, min: e.target.value }))
              }
              onBlur={applyFilters}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="target-max" className="text-xs">
              Max
            </Label>
            <Input
              id="target-max"
              type="number"
              placeholder="Max"
              value={localTargetQuantityRange.max}
              onChange={(e) =>
                setLocalTargetQuantityRange((prev) => ({ ...prev, max: e.target.value }))
              }
              onBlur={applyFilters}
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Progress (% Complete)</h3>
          {(localProgressRange.min || localProgressRange.max) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => {
                setLocalProgressRange({ min: '', max: '' });
                applyFilterChange(() => {
                  setProgressRange(undefined, undefined);
                });
              }}
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label htmlFor="progress-min" className="text-xs">
              Min (%)
            </Label>
            <Input
              id="progress-min"
              type="number"
              placeholder="0"
              min="0"
              max="100"
              value={localProgressRange.min}
              onChange={(e) => setLocalProgressRange((prev) => ({ ...prev, min: e.target.value }))}
              onBlur={applyFilters}
              className="h-8"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="progress-max" className="text-xs">
              Max (%)
            </Label>
            <Input
              id="progress-max"
              type="number"
              placeholder="100"
              min="0"
              max="100"
              value={localProgressRange.max}
              onChange={(e) => setLocalProgressRange((prev) => ({ ...prev, max: e.target.value }))}
              onBlur={applyFilters}
              className="h-8"
            />
          </div>
        </div>
      </div>

      {/* Ending Date Section */}
      <div className="space-y-3 border-t border-border pt-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Ending Date</h3>
          {(selectedEndingTimeframe || date) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() =>
                applyFilterChange(() => {
                  setSelectedEndingTimeframe(undefined);
                  setEndingTimeframe(undefined);
                  setDate(undefined);
                  setEndingDate(undefined);
                })
              }
            >
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          )}
        </div>
        <div className="space-y-2.5">
          <RadioGroup
            value={selectedEndingTimeframe}
            onValueChange={(value) => {
              setSelectedEndingTimeframe(value);
              setDate(undefined);
              applyFilterChange(() => {
                setEndingTimeframe(value);
                setEndingDate(undefined);
              });
            }}
            className="space-y-2.5"
          >
            {ENDING_TIMEFRAMES.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <RadioGroupItem value={option.value} id={`ending-${option.value}`} />
                <Label htmlFor={`ending-${option.value}`} className="text-sm cursor-pointer">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>

          <div className="pt-1">
            <Label className="text-xs mb-1 block">Custom Date Range</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal h-8 text-sm',
                    !date && 'text-muted-foreground',
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={date} onSelect={handleDateSelect} initialFocus />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {hasActiveFilters() && (
        <div className="flex flex-col space-y-2 pt-2 border-t border-border">
          <Button
            variant="outline"
            onClick={resetFilters}
            size={isMobile ? 'default' : 'sm'}
            className="flex items-center justify-center gap-1.5 w-full"
          >
            <FilterX className="h-4 w-4" />
            Reset All Filters
          </Button>
        </div>
      )}
    </div>
  );
}
